import React, { useState, useMemo, useEffect } from "react";
import { supabase } from '@/integrations/supabase/client';
import EmployeeTable from '@/components/employees/EmployeeTable';
import EmployeeDetailModal from './EmployeeDetailModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Employee } from '@/types';
import EditEmployeeForm from './EditEmployeeForm';
import RoleManagementTab from './RoleManagementTab';
import { useRoles } from '@/hooks/employees/useRoles';
import { useEmployees } from '@/hooks/employees/useEmployees';
import { PlusCircle, CheckCircle2, XCircle, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DotyposService } from '@/lib/integrations/dotypos-service';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/ui/use-toast";
import { DotyposExportDialog } from './DotyposExportDialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

interface EmployeeListTabProps {
  businessId: string | null;
}

const EmployeeListTab: React.FC<EmployeeListTabProps> = ({ businessId }) => {
  // Use the optimized useEmployees hook instead of direct database calls
  const { employees: allEmployees, loadingEmployees, refetch } = useEmployees({
    businessId,
    dataLevel: 'standard' // Only fetch what we need for the employee list
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [employeeToDeleteId, setEmployeeToDeleteId] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  // Selected employee for detail view
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string | null>("name"); // Default sort by name
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc"); // Default sort direction ascending
  const [isPosConnected, setIsPosConnected] = useState<boolean | null>(null); // null = checking

  // Contract templates removed - using employment types instead

  // Fetch roles for filtering
  const { data: roles } = useRoles(businessId || '');

  // Check POS connection status
  useEffect(() => {
    const checkPosConnection = async () => {
      if (!businessId) {
        setIsPosConnected(null);
        return;
      }
      try {
        const config = await DotyposService.loadConfig(businessId);
        setIsPosConnected(!!config);
      } catch {
        setIsPosConnected(false);
      }
    };
    checkPosConnection();
  }, [businessId]);

  // Client-side filtering, sorting, and pagination logic
  const filteredAndSortedEmployees = useMemo(() => {
    let filtered = allEmployees || [];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.name?.toLowerCase().includes(search) ||
        emp.email?.toLowerCase().includes(search)
      );
    }

    // Apply role filter
    if (filterRole) {
      filtered = filtered.filter(emp => emp.role_id === filterRole);
    }

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue = a[sortColumn as keyof Employee] || '';
        let bValue = b[sortColumn as keyof Employee] || '';

        // Handle nested role name sorting
        if (sortColumn === 'role_name') {
          aValue = a.roleInfo?.name || '';
          bValue = b.roleInfo?.name || '';
        }

        const comparison = String(aValue).localeCompare(String(bValue));
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [allEmployees, searchTerm, filterRole, sortColumn, sortDirection]);

  // Client-side pagination
  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedEmployees.slice(startIndex, endIndex);
  }, [filteredAndSortedEmployees, currentPage, itemsPerPage]);

  // Update total employees count based on filtered results
  useEffect(() => {
    setTotalEmployees(filteredAndSortedEmployees.length);
  }, [filteredAndSortedEmployees]);

  const handleEmployeeAdded = (newEmployee: Employee) => {
    // Use refetch to get fresh data from cache/database
    refetch();
    setIsAddModalOpen(false); // Close modal after adding
  };

  const handleEmployeeUpdated = (updatedEmployee: Employee) => {
    // Use refetch to get fresh data from cache/database
    refetch();
    setIsEditModalOpen(false);
  };

  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee);
    setIsEditModalOpen(true);
  };

  const handleDeleteEmployee = (employeeId: string) => {
    setEmployeeToDeleteId(employeeId);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDeleteEmployee = async () => {
    if (!employeeToDeleteId) return;

    try {
      const { data: employeeData, error: fetchError } = await supabase
        .from('employees')
        .select('user_id')
        .eq('id', employeeToDeleteId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      const userIdToDelete = employeeData?.user_id;

      if (userIdToDelete) {
        const { error: edgeFunctionError } = await supabase.functions.invoke('delete-employee-user', {
          body: { user_id: userIdToDelete },
        });

        if (edgeFunctionError) {
          console.error('Error calling Edge Function to delete user:', edgeFunctionError);
          throw new Error(`Failed to delete user from auth: ${edgeFunctionError.message}`);
        }
      } else {
        console.warn(`No user_id found for employee ${employeeToDeleteId}. Proceeding with employee record deletion only.`);
      }

      const { error: deleteEmployeeError } = await supabase
        .from('employees')
        .delete()
        .eq('id', employeeToDeleteId);

      if (deleteEmployeeError) {
        throw deleteEmployeeError;
      }

      toast.success('Employee and associated user deleted successfully!');
      // Use refetch to get fresh data from cache/database
      refetch();
    } catch (error: unknown) {
      console.error('Error deleting employee or user:', error);
      toast.error('Failed to delete employee and/or associated user', {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsDeleteConfirmOpen(false);
      setEmployeeToDeleteId(null);
    }
  };

  const handleSendResetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/sign-in`,
      });

      if (error) {
        throw error;
      }

      toast.success('Password reset email sent successfully!', {
        description: `A password reset link has been sent to ${email}.`,
      });
    } catch (error: unknown) {
      console.error('Error sending password reset email:', error);
      toast.error('Failed to send password reset email', {
        description: error instanceof Error ? error.message : String(error),
      });
    }
  };

  // Handle clicking an employee row to view detail
  const handleEmployeeClick = (employee: Employee) => {
    setSelectedEmployee(employee);
  };

  // Contract template functions removed - using employment types instead

  return (
    <div className="space-y-6">
      {/* Employee List Section */}
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <CardTitle className="text-sm font-medium">Employee List</CardTitle>
                {isPosConnected !== null && (
                  isPosConnected ? (
                    <Badge className="flex items-center gap-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">
                      <CheckCircle2 className="w-3 h-3" />
                      POS Connected
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="flex items-center gap-1 text-muted-foreground text-xs">
                      <XCircle className="w-3 h-3" />
                      POS Not Connected
                    </Badge>
                  )
                )}
              </div>
              <div className="flex items-center gap-2">
                {isPosConnected && (
                  <Button
                    variant="outline"
                    onClick={() => setIsExportDialogOpen(true)}
                    disabled={!businessId}
                  >
                    <Upload className="mr-2 h-4 w-4" /> Push to POS
                  </Button>
                )}
                <Button onClick={() => setIsAddModalOpen(true)} disabled={!businessId}>
                  <PlusCircle className="mr-2 h-4 w-4" /> Add Employee
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="w-full sm:w-64"
              />
              <Select
                onValueChange={(value) => {
                  setFilterRole(value === "all" ? null : value);
                  setCurrentPage(1); // Reset to first page on filter change
                }}
                value={filterRole || "all"}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles?.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {totalEmployees >= 10 && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Select
                  onValueChange={(value) => setItemsPerPage(Number(value))}
                  value={itemsPerPage.toString()}
                >
                  <SelectTrigger className="w-full sm:w-[150px]">
                    <SelectValue placeholder="Items per page" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 per page</SelectItem>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                  </SelectContent>
                </Select>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage((prev) => Math.max(1, prev - 1));
                        }}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : undefined}
                      />
                    </PaginationItem>
                    {Array.from({ length: Math.ceil(totalEmployees / itemsPerPage) }, (_, i) => (
                      <PaginationItem key={i}>
                        <PaginationLink
                          href="#"
                          isActive={i + 1 === currentPage}
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(i + 1);
                          }}
                        >
                          {i + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage((prev) =>
                            Math.min(prev + 1, Math.ceil(totalEmployees / itemsPerPage))
                          );
                        }}
                        className={
                          currentPage === Math.ceil(totalEmployees / itemsPerPage)
                            ? "pointer-events-none opacity-50"
                            : undefined
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </div>
          <EmployeeTable
            businessId={businessId}
            employees={paginatedEmployees}
            loading={loadingEmployees}
            onEmployeeAdded={handleEmployeeAdded}
            onEditEmployee={handleEditEmployee}
            onEmployeeUpdated={handleEmployeeUpdated}
            onDeleteEmployee={handleDeleteEmployee}
            onConfirmDeleteEmployee={confirmDeleteEmployee}
            onSendResetPassword={handleSendResetPassword}
            onEmployeeClick={handleEmployeeClick}
            isAddModalOpen={isAddModalOpen}
            onAddModalOpenChange={setIsAddModalOpen}
            isEditModalOpen={isEditModalOpen}
            onEditModalOpenChange={setIsEditModalOpen}
            editingEmployee={editingEmployee}
            isDeleteConfirmOpen={isDeleteConfirmOpen}
            onDeleteConfirmOpenChange={setIsDeleteConfirmOpen}
            employeeToDeleteId={employeeToDeleteId}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={(column) => {
              if (sortColumn === column) {
                setSortDirection(sortDirection === "asc" ? "desc" : "asc");
              } else {
                setSortColumn(column);
                setSortDirection("asc");
              }
              setCurrentPage(1); // Reset to first page on sort change
            }}
          />
        </CardContent>
      </Card>
      </div>

      {/* Role Management Section */}
      <div className="space-y-4">
        <RoleManagementTab businessId={businessId || ''} />
      </div>
      
      {/* Edit Employee Modal - now managed by EmployeeListTab */}
      <EditEmployeeForm
        employee={editingEmployee}
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        onEmployeeUpdated={handleEmployeeUpdated}
      />
      {/* Delete Confirmation Dialog - now managed by EmployeeListTab */}
      <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the employee record and its associated user.
              <br /><br />
              <span className="font-bold text-red-600 dark:text-red-400">Warning:</span> All shifts currently assigned to this employee will become unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteEmployee} className="bg-red-600 hover:bg-red-700">
              Yes, delete employee
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dotypos Export Dialog */}
      {businessId && (
        <DotyposExportDialog
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
          businessId={businessId}
          onExportComplete={() => refetch()}
        />
      )}

      {/* Employee Detail Modal */}
      <EmployeeDetailModal
        open={selectedEmployee !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        businessId={businessId}
        onEmployeeUpdated={(updatedEmployee) => {
          handleEmployeeUpdated(updatedEmployee);
          setSelectedEmployee(updatedEmployee);
        }}
      />
    </div>
  );
};

export default EmployeeListTab;
